import {
  FOLLOW_RING,
  GAME_FONT_FAMILY,
  HP_BAR_OFFSET,
  MAGNET_RING,
  NAME_OFFSET,
  SELF_TAG_INSET,
  SPRITE_FACING_OFFSET,
  SPRITE_SIZE,
  TAG_GAP,
  TAG_RISE,
  TAG_SIZE,
  TEAM_COLORS,
  healthBarWidth,
  hudScaleFor,
} from '../game/constants/feel';
import { legacyDrawnMagnetRadius, shipScale, viewportScale } from '../game/constants/ships';
import { UNNAMED_NAME } from '../game/constants/world';
import { BASE_SPRITE_DARKEN } from '../game/constants/teams';
import type { ControlPointEntity, DroneEntity, Entity, PlayerEntity, World } from '../game/world';
import { COSMETIC_HUES, type AssetStore } from '../assets/asset-store';
import { drawSprite, type Sprite } from '../assets/atlas';
import { darkenHex } from '../assets/tint';
import { EffectSystem } from '../game/effects';
import { Camera } from './camera';
import { BackgroundLayer } from './background-layer';
import { Starfield } from './starfield';
import { drawStatusEffects } from './status-effects';
import { PlayerState } from '../protocol/gen/warinspace/v2/common_pb.js';
import { COUNTER, PHASE, profiler } from '../debug/profiler';

/** `draw` takes a World and a Camera and mutates neither; the loop advances state first. */
export function isDrawableShip(entity: { health: number; team: number }): boolean {
  return entity.health > 0 && entity.team >= 0 && entity.team <= 1;
}

export const MAX_RENDER_SCALE = 2;

export interface RendererOptions {
  /** Moves the star planes and nebulae to composited DOM layers, which forces a transparent canvas. */
  readonly domBackground?: boolean;
}

const MAX_SHIP_COMPOSITES = 128;
const BASE_FALLBACK_COLORS = TEAM_COLORS.map((color) => darkenHex(color, BASE_SPRITE_DARKEN));

export class Renderer {
  private readonly ctx: CanvasRenderingContext2D;
  private readonly effects: EffectSystem;
  private effectsAttached = false;
  private playersDrawn = 0;
  private readonly shipComposites = new Map<string, HTMLCanvasElement>();
  private readonly starfield: Starfield;

  private readonly layer: BackgroundLayer | null;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly assets: AssetStore,
    options: RendererOptions = {},
  ) {
    const ctx = canvas.getContext('2d', {
      alpha: options.domBackground === true,
    });
    if (ctx === null) throw new Error('2D canvas context unavailable');
    this.ctx = ctx;
    this.effects = new EffectSystem(assets);
    this.starfield = new Starfield(assets);
    this.layer = options.domBackground === true ? new BackgroundLayer(canvas, assets) : null;
    if (this.layer !== null) this.effects.mountTrails(this.layer.trailHost);
  }

  dispose(): void {
    this.shipComposites.clear();
    this.effects.dispose();
    this.layer?.dispose();
  }

  resize(camera: Camera): void {
    const ratio = Math.min(window.devicePixelRatio, MAX_RENDER_SCALE);
    const width = Math.max(1, Math.round(this.canvas.clientWidth * ratio));
    const height = Math.max(1, Math.round(this.canvas.clientHeight * ratio));
    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
    }
    camera.resize(width, height, ratio);
  }

  get starfieldSettled(): boolean {
    return this.layer === null ? this.starfield.settled : this.layer.settled;
  }

  draw(world: World, camera: Camera, now: number, menuOpen = false): void {
    const { ctx } = this;
    const { width, height } = this.canvas;
    const prof = profiler.enabled ? profiler : null;

    if (!this.effectsAttached) {
      this.effects.attach(this.canvas, camera);
      this.effectsAttached = true;
    }
    prof?.begin(PHASE.effectsAdvance);
    this.effects.advance(world, camera, now);
    prof?.end(PHASE.effectsAdvance);
    if (prof !== null) {
      const counts = this.effects.counts;
      prof.count(COUNTER.explosions, counts.explosions);
      prof.count(COUNTER.lasers, counts.lasers);
      prof.count(COUNTER.damageNumbers, counts.damageNumbers);
      prof.count(COUNTER.emoji, counts.emoji);
      prof.count(COUNTER.pendingBombs, counts.pendingBombs);
    }

    prof?.begin(PHASE.clear);
    ctx.save();
    if (this.layer === null) {
      ctx.fillStyle = '#04060f';
      ctx.fillRect(0, 0, width, height);
    } else {
      ctx.clearRect(0, 0, width, height);
    }

    ctx.save();
    ctx.scale(camera.worldScale, camera.worldScale);
    ctx.translate(-camera.offsetX, -camera.offsetY);
    prof?.end(PHASE.clear);

    prof?.begin(PHASE.starfield);
    if (this.layer === null) this.starfield.draw(ctx, camera, world.world, now);
    else this.layer.sync(camera, world.world, now);
    prof?.end(PHASE.starfield);
    if (menuOpen) {
      ctx.restore();
      ctx.restore();
      return;
    }
    prof?.begin(PHASE.trailBlit);
    this.effects.drawTrails(ctx, camera);
    prof?.end(PHASE.trailBlit);

    prof?.begin(PHASE.mapAndBases);
    this.drawMapBounds(world);
    this.drawBases(world, now);
    prof?.end(PHASE.mapAndBases);

    prof?.begin(PHASE.scrap);
    for (const entity of world.entities.values()) {
      if (entity.kind === 'scrap') this.drawScrap(entity, camera);
    }
    prof?.end(PHASE.scrap);

    prof?.begin(PHASE.statics);
    for (const entity of world.entities.values()) {
      if (entity.kind === 'turret') this.drawTurret(entity, camera);
      else if (entity.kind === 'controlPoint')
        this.drawControlPoint(entity as ControlPointEntity, camera);
      else if (entity.kind === 'soccerBall') this.drawSoccerBall(entity, camera);
    }
    prof?.end(PHASE.statics);

    prof?.begin(PHASE.dronesUnder);
    for (const entity of world.entities.values()) {
      if (entity.kind === 'drone' && (entity as DroneEntity).isUnder)
        this.drawDrone(entity as DroneEntity, camera);
    }
    prof?.end(PHASE.dronesUnder);

    this.playersDrawn = 0;
    prof?.begin(PHASE.players);
    for (const entity of world.entities.values()) {
      if (entity.kind === 'player' && entity.id !== world.selfId) {
        this.drawPlayer(entity as PlayerEntity, world, camera, now);
      }
    }
    if (world.playing && !world.dead && !world.spectating) this.drawSelf(world, camera, now);
    this.drawFollowRing(world, camera);
    prof?.end(PHASE.players);
    prof?.count(COUNTER.playersDrawn, this.playersDrawn);

    prof?.begin(PHASE.dronesOver);
    for (const entity of world.entities.values()) {
      if (entity.kind === 'drone' && !(entity as DroneEntity).isUnder)
        this.drawDrone(entity as DroneEntity, camera);
    }
    prof?.end(PHASE.dronesOver);

    prof?.begin(PHASE.effectsWorld);
    this.effects.drawWorld(ctx, world, now);
    prof?.end(PHASE.effectsWorld);

    ctx.restore();

    prof?.begin(PHASE.emojiBar);
    ctx.save();
    ctx.scale(camera.uiScale, camera.uiScale);
    if (world.playing && !world.spectating)
      this.effects.drawEmojiBar(ctx, camera, GAME_FONT_FAMILY);
    ctx.restore();
    prof?.end(PHASE.emojiBar);

    ctx.restore();
  }

  private drawFollowRing(world: World, camera: Camera): void {
    const id = world.spectatorFollowId;
    if (id === null) return;
    const entity = world.entities.get(id);
    if (entity === undefined) return;
    const { ctx } = this;
    ctx.save();
    ctx.strokeStyle = FOLLOW_RING.color;
    ctx.globalAlpha = FOLLOW_RING.alpha;
    ctx.lineWidth = FOLLOW_RING.lineWidth / camera.worldScale;
    ctx.beginPath();
    ctx.arc(entity.displayX, entity.displayY, FOLLOW_RING.radius, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  private drawMapBounds(world: World): void {
    const { ctx } = this;
    ctx.save();
    ctx.globalAlpha = 0.5;
    ctx.strokeStyle = '#8899cc';
    ctx.lineWidth = 4;
    ctx.strokeRect(0, 0, world.world.maxX, world.world.maxY);
    ctx.restore();
  }

  /** Bases are never replicated as entities; the client computes their position from the S->C 24 geometry, using `baseRotationSpeed = Infinity` for the stationary mode (dividing by it gives the right answer — do not guard it). */
  private drawBases(world: World, now: number): void {
    const { baseAngle, baseDistance, baseRotationSpeed, maxX, maxY } = world.world;
    if (baseDistance === 0) return;

    const centreX = maxX / 2;
    const centreY = maxY / 2;
    const rotation = baseAngle + (now - world.worldSetupAt) / baseRotationSpeed;

    for (let team = 0; team < 2; team++) {
      const angle = rotation + team * Math.PI;
      const x = centreX + Math.cos(angle) * baseDistance;
      const y = centreY + Math.sin(angle) * baseDistance;
      const sprite = this.assets.tintedSprite('base', team, 0);

      const { ctx } = this;
      ctx.save();
      ctx.translate(x, y);
      if (sprite !== null) {
        ctx.rotate(-angle);
        drawSprite(
          ctx,
          sprite,
          -SPRITE_SIZE.base / 2,
          -SPRITE_SIZE.base / 2,
          SPRITE_SIZE.base,
          SPRITE_SIZE.base,
        );
      } else {
        ctx.fillStyle = BASE_FALLBACK_COLORS[team] ?? '#888';
        ctx.beginPath();
        ctx.arc(0, 0, SPRITE_SIZE.base / 2, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }
  }

  private drawScrap(entity: Entity, camera: Camera): void {
    if (!camera.isVisible(entity.displayX, entity.displayY, SPRITE_SIZE.scrap)) return;
    const sprite = this.assets.scrapSprite(entity.id);
    const size = SPRITE_SIZE.scrap;
    const { ctx } = this;
    if (sprite !== null) {
      /** Derived from the id, not random, or scrap would visibly spin each time it re-enters replication range. */
      ctx.save();
      ctx.translate(entity.displayX, entity.displayY);
      ctx.rotate((entity.id * 2.399963) % (Math.PI * 2));
      drawSprite(ctx, sprite, -size / 2, -size / 2, size, size);
      ctx.restore();
      return;
    }
    ctx.fillStyle = '#EEEE22';
    ctx.fillRect(entity.displayX - size / 4, entity.displayY - size / 4, size / 2, size / 2);
  }

  private drawTurret(entity: Entity, camera: Camera): void {
    if (!camera.isVisible(entity.displayX, entity.displayY, SPRITE_SIZE.turret)) return;
    const size = SPRITE_SIZE.turret;
    const sprite = this.assets.tintedSprite(
      'turret',
      entity.team,
      (entity as { turretType?: number }).turretType ?? 0,
    );
    const { ctx } = this;
    if (sprite !== null) {
      ctx.save();
      ctx.translate(entity.displayX, entity.displayY);
      ctx.rotate(entity.displayAngle + SPRITE_FACING_OFFSET);
      drawSprite(ctx, sprite, -size / 2, -size / 2, size, size);
      ctx.restore();
    } else {
      ctx.fillStyle = TEAM_COLORS[entity.team] ?? '#888';
      ctx.fillRect(entity.displayX - size / 2, entity.displayY - size / 2, size, size);
    }
    this.drawHealthBar(
      entity.displayX,
      entity.displayY - HP_BAR_OFFSET,
      healthBarWidth(1),
      entity.health,
      entity.maxHealth,
    );
  }

  private drawControlPoint(entity: ControlPointEntity, camera: Camera): void {
    if (!camera.isVisible(entity.displayX, entity.displayY, SPRITE_SIZE.controlPoint)) return;
    const { ctx } = this;
    const size = SPRITE_SIZE.controlPoint;
    const sprite = this.assets.controlPointSprite(entity.pointType);
    if (sprite !== null) {
      drawSprite(ctx, sprite, entity.displayX - size / 2, entity.displayY - size / 2, size, size);
    }
    ctx.save();
    ctx.strokeStyle = '#DDD';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(entity.displayX, entity.displayY, 500, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  private drawSoccerBall(entity: Entity, camera: Camera): void {
    if (!camera.isVisible(entity.displayX, entity.displayY, SPRITE_SIZE.soccerBall)) return;
    const sprite = this.assets.soccerBallSprite();
    const size = SPRITE_SIZE.soccerBall;
    if (sprite !== null) {
      drawSprite(
        this.ctx,
        sprite,
        entity.displayX - size / 2,
        entity.displayY - size / 2,
        size,
        size,
      );
    }
  }

  private drawDrone(entity: DroneEntity, camera: Camera): void {
    if (!entity.ownerKnown) return;
    if (!camera.isVisible(entity.displayX, entity.displayY, SPRITE_SIZE.drone)) return;
    const size = SPRITE_SIZE.drone;
    const sprite = this.assets.tintedSprite('drone', entity.team, entity.droneType);
    const { ctx } = this;
    ctx.save();
    ctx.translate(entity.displayX, entity.displayY);
    ctx.rotate(entity.displayAngle + SPRITE_FACING_OFFSET);
    if (sprite !== null) {
      drawSprite(ctx, sprite, -size / 2, -size / 2, size, size);
    } else {
      ctx.fillStyle = TEAM_COLORS[entity.team] ?? '#888';
      ctx.beginPath();
      ctx.arc(0, 0, size / 3, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  private drawPlayer(entity: PlayerEntity, world: World, camera: Camera, now: number): void {
    if (!isDrawableShip(entity)) return;
    const scale = shipScale(entity.shipId);
    const size = SPRITE_SIZE.playerBase * scale;
    if (!camera.isVisible(entity.displayX, entity.displayY, size)) return;
    this.playersDrawn += 1;

    const hudScale = hudScaleFor(viewportScale(entity.shipId));

    const { ctx } = this;

    drawStatusEffects(
      ctx,
      entity.states,
      entity.displayX,
      entity.displayY,
      hudScale,
      now,
      this.assets,
    );

    ctx.save();

    if (entity.states.has(PlayerState.STEALTH)) ctx.globalAlpha = 0.3;

    ctx.translate(entity.displayX, entity.displayY);
    ctx.rotate(entity.displayAngle + SPRITE_FACING_OFFSET);
    const sprite = this.wornShip(
      entity.shipId,
      entity.team,
      entity.skinProductId,
      entity.decalProductId,
      world,
      size * camera.worldScale,
    );
    if (sprite !== null) {
      drawSprite(ctx, sprite, -size / 2, -size / 2, size, size);
    } else {
      ctx.rotate(-SPRITE_FACING_OFFSET);
      ctx.fillStyle = TEAM_COLORS[entity.team] ?? '#888';
      ctx.beginPath();
      ctx.moveTo(size / 2, 0);
      ctx.lineTo(-size / 2, -size / 3);
      ctx.lineTo(-size / 2, size / 3);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();

    this.drawHealthBar(
      entity.displayX,
      entity.displayY - HP_BAR_OFFSET * hudScale,
      healthBarWidth(hudScale),
      entity.health,
      entity.maxHealth,
    );

    const cachedName = world.nameFor(entity.nameId);
    let nameWidth = 0;
    if (cachedName !== null) {
      const name = cachedName === '' ? UNNAMED_NAME : cachedName;
      ctx.save();
      ctx.fillStyle = TEAM_COLORS[entity.team] ?? '#CCC';
      ctx.font = `${String(Math.round(14 * hudScale))}px ${GAME_FONT_FAMILY}, sans-serif`;
      ctx.textAlign = 'center';
      nameWidth = ctx.measureText(name).width;
      ctx.fillText(name, entity.displayX, entity.displayY - NAME_OFFSET * hudScale);
      ctx.restore();
    }

    this.drawTag(
      entity.tagProductId,
      world,
      entity.displayX - nameWidth / 2 - TAG_GAP,
      entity.displayY - NAME_OFFSET * hudScale - TAG_RISE.other,
    );
  }

  /** Hull, skin and decal as one sprite: all three land in the same square, so three rotated blits paint the same pixels. */
  private wornShip(
    shipId: number,
    team: number,
    skinProductId: number,
    decalProductId: number,
    world: World,
    devicePx: number,
  ): Sprite | null {
    const hull = this.assets.tintedSpriteFor('ship', team, shipId, devicePx);
    if (hull === null) return null;
    const skin = this.wornSprite(skinProductId, 'skin', team, world, devicePx);
    const decal = this.wornSprite(decalProductId, 'decal', team, world, devicePx);
    if (skin === null && decal === null) return hull;

    const width = Math.max(hull.sw, skin?.sw ?? 0, decal?.sw ?? 0);
    const key = `${String(shipId)}:${String(team)}:${String(skinProductId)}:${String(decalProductId)}:${String(width)}`;
    const cached = this.shipComposites.get(key);
    if (cached !== undefined) {
      return { image: cached, sx: 0, sy: 0, sw: cached.width, sh: cached.height };
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = width;
    const layerCtx = canvas.getContext('2d');
    if (layerCtx === null) return hull;
    layerCtx.imageSmoothingEnabled = true;
    layerCtx.imageSmoothingQuality = 'high';
    for (const layer of [hull, skin, decal]) {
      if (layer === null) continue;
      layerCtx.drawImage(layer.image, layer.sx, layer.sy, layer.sw, layer.sh, 0, 0, width, width);
    }
    if (this.shipComposites.size >= MAX_SHIP_COMPOSITES) this.shipComposites.clear();
    this.shipComposites.set(key, canvas);
    return { image: canvas, sx: 0, sy: 0, sw: width, sh: width };
  }

  private wornSprite(
    productId: number,
    kind: 'decal' | 'skin',
    team: number,
    world: World,
    devicePx: number,
  ): Sprite | null {
    if (productId <= 0) return null;
    const product = world.products.get(productId);
    if (product === undefined) return null;
    const shift = product.hued ? (COSMETIC_HUES[kind][team] ?? null) : null;
    return this.assets.cosmeticSpriteFor(product.imagePath, shift, devicePx);
  }

  private drawTag(productId: number, world: World, centreX: number, centreY: number): void {
    if (productId <= 0) return;
    const product = world.products.get(productId);
    if (product === undefined) return;
    const sprite = this.assets.cosmeticSprite(product.imagePath, null);
    if (sprite === null) return;
    drawSprite(
      this.ctx,
      sprite,
      centreX - TAG_SIZE / 2,
      centreY - TAG_SIZE / 2,
      TAG_SIZE,
      TAG_SIZE,
    );
  }

  /** Drawn from selfX/selfY, not the replicated entity — the client is authoritative for its own movement, so the server's echo is a round trip stale. */
  private drawSelf(world: World, camera: Camera, now: number): void {
    const { ctx } = this;
    const scale = shipScale(world.selfShipId);
    const size = SPRITE_SIZE.playerBase * scale;
    const hudScale = hudScaleFor(viewportScale(world.selfShipId));

    const ringRadius = legacyDrawnMagnetRadius(world.selfShipId);
    if (ringRadius > 0) {
      ctx.save();
      ctx.globalAlpha = MAGNET_RING.alpha;
      ctx.strokeStyle = MAGNET_RING.color;
      ctx.lineWidth = MAGNET_RING.lineWidth;
      ctx.beginPath();
      ctx.arc(world.selfX, world.selfY, ringRadius, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    drawStatusEffects(ctx, world.selfStates, world.selfX, world.selfY, hudScale, now, this.assets);

    ctx.save();
    if (world.selfStates.has(PlayerState.STEALTH)) ctx.globalAlpha = 0.3;
    ctx.translate(world.selfX, world.selfY);
    ctx.rotate(world.selfAngle + SPRITE_FACING_OFFSET);
    const sprite = this.wornShip(
      world.selfShipId,
      world.selfTeam,
      world.equipment.shipSkins[world.selfShipId] ?? 0,
      world.equipment.decal,
      world,
      size * camera.worldScale,
    );
    if (sprite !== null) {
      drawSprite(ctx, sprite, -size / 2, -size / 2, size, size);
    } else {
      ctx.rotate(-SPRITE_FACING_OFFSET);
      ctx.fillStyle = '#FFFFFF';
      ctx.beginPath();
      ctx.moveTo(size / 2, 0);
      ctx.lineTo(-size / 2, -size / 3);
      ctx.lineTo(-size / 2, size / 3);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();

    this.drawHealthBar(
      world.selfX,
      world.selfY - HP_BAR_OFFSET * hudScale,
      healthBarWidth(hudScale),
      world.selfHealth,
      world.selfMaxHealth,
    );

    const selfName = world.selfName;
    let selfNameWidth = 0;
    if (selfName !== null && selfName !== '') {
      ctx.save();
      ctx.fillStyle = TEAM_COLORS[world.selfTeam] ?? '#CCC';
      ctx.font = `${String(Math.round(14 * hudScale))}px ${GAME_FONT_FAMILY}, sans-serif`;
      ctx.textAlign = 'center';
      selfNameWidth = ctx.measureText(selfName).width;
      ctx.fillText(selfName, world.selfX, world.selfY - NAME_OFFSET * hudScale);
      ctx.restore();
    }

    this.drawTag(
      world.equipment.tag,
      world,
      Math.min(world.selfX - selfNameWidth / 2 - TAG_GAP, world.selfX - SELF_TAG_INSET),
      world.selfY - NAME_OFFSET * hudScale - TAG_RISE.self,
    );
  }

  private drawHealthBar(
    centreX: number,
    y: number,
    width: number,
    health: number,
    maxHealth: number,
  ): void {
    if (maxHealth <= 0) return;
    const fraction = Math.max(0, Math.min(1, health / maxHealth));
    const barWidth = Math.max(width, 40);
    const barHeight = Math.max(3, barWidth / 14);
    const left = centreX - barWidth / 2;

    const { ctx } = this;
    ctx.save();
    ctx.fillStyle = '#111111';
    ctx.fillRect(left, y, barWidth, barHeight);
    const inset = 1;
    ctx.fillStyle = '#FF1111';
    ctx.fillRect(left + inset, y + inset, barWidth - inset * 2, barHeight - inset * 2);
    ctx.fillStyle = '#11FF11';
    ctx.fillRect(left + inset, y + inset, (barWidth - inset * 2) * fraction, barHeight - inset * 2);

    const segments = Math.floor(maxHealth / 100);
    if (segments > 1 && segments <= 40) {
      ctx.strokeStyle = '#111111';
      ctx.lineWidth = 2;
      ctx.beginPath();
      for (let i = 1; i < segments; i++) {
        const x = left + inset + ((barWidth - inset * 2) * i) / segments;
        ctx.moveTo(x, y + inset);
        ctx.lineTo(x, y + barHeight - inset);
      }
      ctx.stroke();
    }
    ctx.restore();
  }
}
